import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DigitalSignatureGateway from "../../../api/gateways/DigitalSignatureGateway";
import useBaseRequest from "../../../api/BaseRequest";
import { useDispatch } from "react-redux";
import { documentsActions } from "../../../store/documents/DocumentReducer";
import { BlobCache } from "../utils/BlobCache";
import { extractQueryParamValue } from "../utils/QueryParams";

export const useVerifyQueryParamsAndConfirmEmail = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isError, setIsError] = useState(false);

  const { execute: getWebSigningDocument } = useBaseRequest(
    (payload: { id: string; data: { email: string; token: string } }) =>
      DigitalSignatureGateway.getWebSigningDocument(payload.id, payload.data),
    {
      onCompleted: (response) => {
        const blob = response.data;

        BlobCache.set(blob);
        const blobUrl = URL.createObjectURL(blob);

        const metaBase64 = response.headers["x-document-meta"];
        const documentData = metaBase64 ? JSON.parse(atob(metaBase64)) : null;

        dispatch(documentsActions.setDocumentBlobUrl(blobUrl));
        dispatch(documentsActions.setFetchedDocument(documentData));
      },
      onError: async (error) => {
        let errorMessage = error.message;

        // Try to extract message from blob
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            errorMessage = json?.message || json?.error || error.message;
          } catch (e) {
            console.warn("Failed to parse error blob", e);
          }
        }

        if (errorMessage === "Zahtev za digitalnim potpisom je već potpisan!") {
          setIsError(false);
          dispatch(documentsActions.setDocumentSuccessfullySignedStatus(true));
          return;
        }

        setIsError(true);
      },
    }
  );

  const { execute: verifyTokenHandler, loading: isLoading } = useBaseRequest(
    DigitalSignatureGateway.confirmEmailBeforeSigningPdf,
    {
      onError: (err) => {
        if (err.message === "Zahtev za digitalnim potpisom mora biti u 'PENDING' statuts!") {
          setIsError(false);

          getWebSigningDocument({
            id: extractQueryParamValue("id") || "",
            data: {
              email: extractQueryParamValue("email") || "",
              token: extractQueryParamValue("token") || "",
            },
          });

          return;
        }
        setIsError(true);
      },
      onCompleted: () => {
        getWebSigningDocument({
          id: extractQueryParamValue("id") || "",
          data: {
            email: extractQueryParamValue("email") || "",
            token: extractQueryParamValue("token") || "",
          },
        });
      },
    }
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = extractQueryParamValue("token");
    const email = extractQueryParamValue("email");
    const documentId = extractQueryParamValue("id");

    const keys = Array.from(searchParams.keys());
    const expectedKeys = ["token", "email", "id"];

    const isValidParams =
      keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key));

    if (!isValidParams || !token || !email || !documentId) {
      setIsError(true);
      return;
    }

    verifyTokenHandler({ token, email });
  }, [location.search]);

  return { isLoading, isError };
};
